import { useAppSettingsSlice } from 'app/slice';
import {
  selectAppSettings,
  selectglobalSettings,
  selectLoading,
} from 'app/slice/selectors';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IBasePageProps } from '../IBasePageProps';
import { LoginWrapper } from './Components/LoginWrapper';
import {
  LoginState,
  TwoFactorAuthenticationParams,
  UserPasswordRemember,
} from './Components/type';
import { useLoginSlice } from './slice';
import {
  CodeVerificationModel,
  LoginUserModel,
  ResponseLoginUserModel,
  SignInFailureReasons,
  SignInStatus,
} from './slice/types';
import { api } from './slice/saga';
import { useLoginState } from './Hooks/useLoginState';
import { trimStart } from 'lodash';
import { IsModuleEnabled } from 'types/AppSettings';
import { KnownModules } from 'types/KnownModules';
import { AllowedSettings } from 'utils/globalSettings';
import { DetectIsMobile } from 'utils/mobileDetect';
import { useLocation } from 'react-router-dom';
import { toRootedURL } from 'utils/url-utils';
import { useEffectOnMount } from 'app/hooks/useEffectOnMount';
import { ScanTarget } from 'app/slice/types';

/**
 *
 * LoginPage
 *
 */
export interface LoginPageProps extends IBasePageProps {}

export function LoginPage(props: LoginPageProps) {
  //const history = useHistory();
  const dispatch = useDispatch();
  const { actions } = useAppSettingsSlice();
  const { actions: appSettingsActions } = useAppSettingsSlice();
  const location = useLocation();
  //const queryProps = getParams(params, props);
  const settings = useSelector(selectglobalSettings);
  const appSettings = useSelector(selectAppSettings);
  const { actions: loginActions } = useLoginSlice();
  const isLoading = useSelector(selectLoading);
  const [resposeData, setResposeData] = React.useState<
    ResponseLoginUserModel
  >();
  const [rememberMe, setRememberMe] = React.useState<boolean>(false);
  const [processing, setProcessiong] = React.useState<boolean>(false);
  const isMobile = DetectIsMobile();
  const showSSO = React.useMemo(() => {
    return (
      IsModuleEnabled(appSettings, KnownModules.ShibbolethAuthentication) ||
      IsModuleEnabled(appSettings, KnownModules.OAuth)
    );
  }, [appSettings]);

  const showNewUser = React.useMemo(() => {
    return (
      settings.GetBoolean(
        AllowedSettings[AllowedSettings.NewUsersSignupForm],
      ) &&
      !settings.GetBoolean(
        AllowedSettings[AllowedSettings.HideLinkToRegistrationForm],
      )
    );
  }, [settings]);

  const showRememberMe = React.useMemo(() => {
    /*if (isMobile) {
      return false;
    } else {*/
    const v = settings.GetString(AllowedSettings.SessionTimeout_Persistent);
    if (v.trim() !== '') {
      return parseInt(v) > 0;
    } else return false;
    //}
  }, [settings]);
  const [loginState, setLoginState] = React.useState<LoginState>(
    //'TwoFactorAuthentication',
    showSSO ? 'SSOON' : 'Login_SSOOFF',
  );
  const [prevLoginState, setPrevLoginState] = React.useState<LoginState>(
    showSSO ? 'SSOON' : 'Login_SSOOFF',
  );
  const checkLoginResult = React.useCallback(
    (data: ResponseLoginUserModel) => {
      switch (data.Result) {
        case SignInStatus.Success:
          dispatch(loginActions.clearBadAttempts());
          if (data.RedirectUrl !== null) {
            let url = toRootedURL(trimStart(data.RedirectUrl, '~'));
            if (url.includes('aspx')) {
              url = toRootedURL(url);
            }
            window.location.replace(url);
            //history.push(url);
          }
          break;
        case SignInStatus.RequiresVerification:
          setProcessiong(false);
          setPrevLoginState(loginState);
          setLoginState('TwoFactorAuthentication');
          break;
        case SignInStatus.LockedOut:
          data.ErrorMessages.forEach(e => {
            dispatch(
              actions.addNotification({
                variant: 'error',
                message: e,
              }),
            );
          });
          break;
        case SignInStatus.Failure:
          switch (data.FailureReasons) {
            case SignInFailureReasons.MustChangePassword:
            case SignInFailureReasons.PasswordExpired:
              if (data.RedirectUrl !== null) {
                let url = toRootedURL(trimStart(data.RedirectUrl, '~'));
                if (url.includes('aspx')) {
                  url = toRootedURL(url);
                }
                window.location.replace(url);
                //history.push(url);
              }
              break;
            default:
              dispatch(loginActions.increaseBadAttempts());
              data.ErrorMessages.forEach(e => {
                dispatch(
                  actions.addNotification({
                    variant: 'error',
                    message: e,
                  }),
                );
              });
              break;
          }
          break;
        default:
          break;
      }
    },
    [actions, dispatch, loginActions, loginState],
  );
  const handleSSOOFFSubmit = React.useCallback(
    async (values: UserPasswordRemember) => {
      const model: LoginUserModel = {
        ...values,
        PageUrl: window.location.href,
      };
      setProcessiong(true);
      setRememberMe(model.RememberMe);
      try {
        const data = await api.LoginUser(model);
        setResposeData(data);
        dispatch(loginActions.login_Success(data));
        checkLoginResult(data);
      } catch (error) {
        console.error(error);
        dispatch(loginActions.login_Error(error as string));
      }
      setProcessiong(false);
    },
    [checkLoginResult, dispatch, loginActions],
  );
  const handleSSO_ON_Submit = React.useCallback(async () => {
    setProcessiong(true);
    try {
      const params = new URLSearchParams(location.search);
      const param = params.get('ReturnUrl');
      const returnUrl = param !== null ? trimStart(param, '~') : '';
      const url = await api.SSOLoginUrl(returnUrl);
      window.location.replace(trimStart(url, '~'));
    } catch (error) {
      console.error(error);
      dispatch(
        actions.addNotification({
          variant: 'error',
          message: error as string,
        }),
      );
    }
    setProcessiong(false);
  }, [actions, dispatch, location.search]);
  const handleSSO_OFF_Submit = React.useCallback(() => {
    setLoginState('Login_SSOON');
  }, []);
  const handle2FASubmit = React.useCallback(
    async (values: TwoFactorAuthenticationParams | null) => {
      if (
        resposeData !== undefined &&
        resposeData.Provider !== null &&
        values !== null &&
        values.one &&
        values.two &&
        values.three &&
        values.four &&
        values.five &&
        values.six
      ) {
        const code =
          values.one +
          values.two +
          values.three +
          values.four +
          values.five +
          values.six;
        setProcessiong(true);
        try {
          const data = await api.CodeVerification({
            Provider: resposeData.Provider,
            Code: code,
            RememberMe: rememberMe,
          } as CodeVerificationModel);
          if (data.Result === SignInStatus.Success) {
            dispatch(loginActions.clearBadAttempts());
            if (resposeData.RedirectUrl !== null) {
              const url = trimStart(resposeData.RedirectUrl, '~');
              window.location.replace(url);
              //history.push(url);
            }
          } else {
            data.ErrorMessages.forEach(e => {
              dispatch(
                actions.addNotification({
                  variant: 'error',
                  message: e,
                }),
              );
            });
          }
        } catch (error) {
          dispatch(
            actions.addNotification({
              variant: 'error',
              message: error as string,
            }),
          );
        }
        setProcessiong(false);
      }
      setLoginState(prevLoginState);
    },
    [actions, dispatch, loginActions, prevLoginState, rememberMe, resposeData],
  );
  const reSend = React.useCallback(async () => {
    setProcessiong(true);
    const rd = { ...resposeData } as ResponseLoginUserModel;
    if (rd !== undefined) {
      try {
        const data = await api.SendVerificationCode();
        rd.Provider = data.Provider;
        setResposeData(rd);
        dispatch(loginActions.setProvider(data.Provider));
      } catch (error) {
        dispatch(
          actions.addNotification({
            variant: 'error',
            message: error as string,
          }),
        );
      }
    }
    setProcessiong(false);
  }, [actions, dispatch, loginActions, resposeData]);

  const [pageTitle, pageDescription, MainContent] = useLoginState(
    loginState,
    showNewUser,
    showRememberMe,
    isMobile,
    handleSSOOFFSubmit,
    handleSSO_ON_Submit,
    handleSSO_OFF_Submit,
    handle2FASubmit,
    reSend,
  );
  useEffectOnMount(() => {
    if (new URLSearchParams(location.search).has('msg')) {
      const msg = new URLSearchParams(location.search).get('msg');
      if (msg) {
        dispatch(
          actions.addNotification({
            variant: 'error',
            message: msg,
          }),
        );
      }
    }
  });
  useEffectOnMount(() => {
    dispatch(appSettingsActions.setBarcodeScannigTarget(ScanTarget.Login));
    return () => {
      dispatch(appSettingsActions.setBarcodeScannigTarget(ScanTarget.All));
    };
  });
  React.useEffect(() => {
    showSSO ? setLoginState('SSOON') : setLoginState('Login_SSOOFF');
  }, [showSSO]);

  return isLoading ? (
    <>{''}</>
  ) : (
    <LoginWrapper
      help_Login={appSettings?.Help_Login}
      pageTitle={pageTitle}
      showTopProcessing={processing}
      pageDescription={pageDescription}
      disabled={processing}
      contentMaxWidth={600}
    >
      {!!MainContent ? MainContent : <></>}
    </LoginWrapper>
  );
}
